import React, { useEffect, useRef, useState } from 'react';
import { Film, Upload, X, Loader2, Send, CheckCircle2, AlertTriangle } from 'lucide-react';

/**
 * EpisodeLineupModal — admin / PM submits ONE full-episode MP4 (the
 * assembled animation lineup) straight to client review.
 *
 * Founder spec (Feb 2026): clients (e.g. Chloë) do NOT want to review
 * individual shots for Layout & Animation. They want to see the FULL
 * 12-minute episode cut as ONE video and annotate / comment on the
 * timeline like they do for animatics.
 *
 * Why this exists separately from the per-shot row uploader:
 *   • Per-shot uploads are tied to a `task.id` via `item_refs` so the
 *     pipeline can track approval per shot. The lineup is a roll-up —
 *     it has no single shot ref, it just represents the whole episode.
 *   • The lineup commonly exceeds the per-shot file size guidance
 *     (500 MB+ for a 12-min 1080p H.264). We surface a clear progress
 *     bar and a friendly cap warning rather than a generic upload err.
 *   • The client review experience is identical to Animatics — the
 *     submission flows through the same /reviews/submit → ClientReviewPage
 *     pipeline with full timeline-pinned annotations + chat.
 *
 * Props:
 *   - api          : authed axios instance
 *   - projectId    : episode (or standalone project) id
 *   - projectName  : for the modal title
 *   - bucketStage  : "animation" | "lighting" | "comp" | … — passed
 *                    straight into /reviews/submit `stage`
 *   - bucketLabel  : "Layout & Animation" | "Lighting" | … — for titles
 *   - canSkipInternal : true for admin / PM / supervisor / CD
 *   - onClose      : () => void
 *   - onSubmitted  : (submissionId) => void  — refresh parent list
 */
const MAX_BYTES = 2 * 1024 * 1024 * 1024;  // 2 GB hard cap (matches backend)
const RECOMMENDED_BYTES = 500 * 1024 * 1024;  // 500 MB founder default
// Founder bug Feb 2026 (production vawworld.in): Cloudflare's 100 MB
// request-body cap killed the 367 MB lineup upload with a 413 AFTER
// the upload bar hit 100%. We now switch to chunked upload as soon as
// the file crosses the safe threshold below — each chunk is ≤ 50 MB
// so every HTTP request stays well under CF's 100 MB edge limit.
const CHUNK_THRESHOLD = 50 * 1024 * 1024;
const CHUNK_BYTES = 50 * 1024 * 1024;
const VIDEO_EXT_RE = /\.(mp4|mov|webm|m4v|mkv)$/i;

export default function EpisodeLineupModal({
  api, projectId, projectName, bucketStage, bucketLabel,
  canSkipInternal, onClose, onSubmitted,
}) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState(`${projectName || 'Episode'} — Full ${bucketLabel || 'Animation'} Lineup`);
  const [description, setDescription] = useState('');
  const [skipInternal, setSkipInternal] = useState(canSkipInternal);  // default direct-to-client for admin/PM
  const [uploading, setUploading] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [warn, setWarn] = useState('');
  const [success, setSuccess] = useState('');
  // Founder fix Jun 25 2026 — when the chunked upload hits 100 % the
  // backend pipeline (cloud-put + NAS + transcode) keeps running in a
  // background task. We poll /result and surface the live stage here
  // so the user sees real progress ("Saving to cloud storage…") instead
  // of an indefinite spinner. Empty string = no finalisation in flight.
  const [finaliseStage, setFinaliseStage] = useState('');
  const fileRef = useRef(null);

  // Pre-fill the title with episode + dept context so the client sees
  // a meaningful card title in their inbox.
  useEffect(() => {
    if (!projectName) return;
    setTitle(`${projectName} — Full ${bucketLabel || 'Animation'} Lineup`);
  }, [projectName, bucketLabel]);

  const pickFile = (f) => {
    setError(''); setWarn(''); setSuccess('');
    if (!f) { setFile(null); return; }
    const ok = (f.type || '').startsWith('video/') || VIDEO_EXT_RE.test(f.name);
    if (!ok) {
      setError(`Only video files (MP4 / MOV / WEBM / MKV) are accepted for the episode lineup. Got: ${f.name}`);
      return;
    }
    if (f.size > MAX_BYTES) {
      setError(`File is ${(f.size / 1024 / 1024 / 1024).toFixed(2)} GB — max is 2 GB. Split into two halves or compress to H.264 720p.`);
      return;
    }
    if (f.size > RECOMMENDED_BYTES) {
      setWarn(`Large file: ${(f.size / 1024 / 1024).toFixed(0)} MB. Upload may take several minutes. Keep the tab open.`);
    }
    setFile(f);
  };

  const submit = async () => {
    if (!file) { setError('Pick the episode lineup MP4 first.'); return; }
    if (!title.trim()) { setError('Title is required so the client sees a clear card.'); return; }
    setUploading(true); setSubmitting(false); setError(''); setSuccess('');
    setProgressPct(0);
    try {
      // 1) Upload — pick single-shot for small files, chunked for anything
      //    above the 50 MB Cloudflare-safe threshold. Both code paths
      //    return the same media-descriptor shape so the downstream
      //    /reviews/submit call is identical.
      let media;
      if (file.size > CHUNK_THRESHOLD) {
        media = await uploadChunked(file);
      } else {
        const fd = new FormData();
        fd.append('project_id', projectId);
        fd.append('file', file);
        fd.append('preset', 'web-720');
        fd.append('stage', bucketStage || 'animation');
        fd.append('kind', 'episode_lineup');
        fd.append('parent_code', 'EPISODE_LINEUP');
        fd.append('chapter_name', projectName || '');
        const r = await api.post('/reviews/upload-media', fd, {
          onUploadProgress: (e) => {
            if (e.total) setProgressPct(Math.round((e.loaded / e.total) * 100));
          },
        });
        media = r.data;
      }
      setUploading(false);
      setSubmitting(true);

      // 2) Create the review submission (unchanged for both paths).
      const payload = {
        project_id: projectId,
        kind: 'episode_lineup',
        stage: bucketStage || 'animation',
        title: title.trim(),
        description: description.trim() || `Full ${bucketLabel || 'animation'} cut for ${projectName || 'episode'} — ready for client review.`,
        item_refs: [],
        media: [media],
        ...(skipInternal ? { skip_internal: true } : {}),
      };
      const { data: sub } = await api.post('/reviews/submit', payload);
      setSuccess(`Submitted ✓ Submission id: ${sub.id?.slice(0, 8) || ''} — the client will see it in their Approvals inbox.`);
      setSubmitting(false);
      if (onSubmitted) onSubmitted(sub.id);
    } catch (e) {
      setUploading(false); setSubmitting(false);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const detailStr = typeof detail === 'string'
        ? detail
        : (detail?.message || detail?.error || (detail && JSON.stringify(detail).slice(0, 200)) || '');
      // 413 = Cloudflare proxy edge-rejected the request body. Our
      // chunked path should defeat this, but if the small-file
      // single-shot upload trips a tighter edge limit later we want
      // the founder to see a clear next-step.
      if (status === 413) {
        setError(
          `Upload too large for the proxy (HTTP 413). This file is ${(file.size / 1024 / 1024).toFixed(0)} MB. ` +
          `Files above 50 MB are auto-split into 50 MB chunks — if you still see this, refresh and try again.`
        );
      } else {
        setError(`Failed${status ? ' (HTTP ' + status + ')' : ''}: ${detailStr || e.message || 'unknown error'}`);
      }
    }
  };

  /**
   * Chunked uploader — splits the file into ≤ 50 MB parts so every
   * HTTP request stays under Cloudflare's 100 MB edge cap. Backend
   * endpoints: /reviews/upload-chunked/{init,part,complete}.
   * Returns the same media descriptor shape as /reviews/upload-media.
   *
   * Founder bug Jun 24 2026: a 303 MB lineup upload failed with
   * "Failed: Network Error" because the Mac slept / wifi-switched
   * mid-upload. Console showed ERR_NETWORK_IO_SUSPENDED +
   * ERR_NETWORK_CHANGED. The old uploader retried each chunk only
   * ONCE with 800 ms backoff — way too tight for a real laptop
   * sleep/wake (5-30 s) or wifi handoff. Result: chunks failed,
   * the green ✓ on the file was misleading (it only meant
   * "selected"), and the lineup never actually reached the client.
   *
   * Fix: 8 attempts per chunk with exponential backoff (1-30 s),
   * wait for `navigator.onLine` before retrying, surface live
   * "Reconnecting" status to the UI, and bail with a clear error
   * after ~95 s of continuous failure.
   */
  const uploadChunked = async (f) => {
    const initRes = await api.post('/reviews/upload-chunked/init', {
      filename: f.name,
      size: f.size,
      project_id: projectId,
      stage: bucketStage || 'animation',
      kind: 'episode_lineup',
      parent_code: 'EPISODE_LINEUP',
      chapter_name: projectName || '',
      preset: 'web-720',
      content_type: f.type || 'video/mp4',
    });
    const uploadId = initRes.data.upload_id;
    const chunkSize = initRes.data.chunk_size || CHUNK_BYTES;
    const totalParts = Math.ceil(f.size / chunkSize);

    // Wait until navigator.onLine is true. Resolves immediately if
    // already online; otherwise listens for the 'online' event and
    // wakes up as soon as the network returns.
    const waitForOnline = () => new Promise((resolve) => {
      if (typeof navigator === 'undefined' || navigator.onLine !== false) {
        resolve();
        return;
      }
      const handler = () => {
        window.removeEventListener('online', handler);
        resolve();
      };
      window.addEventListener('online', handler);
      // Safety: also poll every 2 s in case the 'online' event misfires
      // (Chrome can be flaky after sleep). Capped via the outer retry budget.
      const poller = setInterval(() => {
        if (navigator.onLine) {
          clearInterval(poller);
          window.removeEventListener('online', handler);
          resolve();
        }
      }, 2000);
    });

    // Treat ANY network/timeout error as retryable. Server errors
    // (HTTP 4xx/5xx) propagate up — those mean the chunk is bad
    // and retrying won't help.
    const isRetryableNetworkError = (err) => {
      if (!err) return false;
      // No HTTP response = network layer error (DNS, TCP reset, etc.)
      if (!err.response) return true;
      // 5xx and 429 = transient server issues
      const s = err.response?.status;
      if (s >= 500 || s === 429 || s === 408) return true;
      return false;
    };

    const MAX_ATTEMPTS = 8;
    const BACKOFF_MS = [1000, 2000, 4000, 8000, 16000, 30000, 30000];

    for (let i = 0; i < totalParts; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, f.size);
      const slice = f.slice(start, end);
      const fd = new FormData();
      fd.append('upload_id', uploadId);
      fd.append('part_index', String(i));
      fd.append('part', slice, f.name);

      let lastErr;
      let succeeded = false;
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        try {
          // Wait for network to come back BEFORE issuing the request —
          // saves a guaranteed-failed POST when the laptop is asleep.
          await waitForOnline();
          await api.post('/reviews/upload-chunked/part', fd);
          succeeded = true;
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
          if (!isRetryableNetworkError(e)) {
            // Non-transient: don't waste retries.
            break;
          }
          // Surface "reconnecting" status to the UI. Re-use setError
          // as a soft status indicator; cleared on success below.
          const remaining = MAX_ATTEMPTS - attempt - 1;
          if (remaining > 0) {
            setError(
              `Network paused — reconnecting (attempt ${attempt + 2}/${MAX_ATTEMPTS}, part ${i + 1}/${totalParts}). Keep this tab open.`
            );
          }
          const backoff = BACKOFF_MS[attempt] ?? 30000;
          await new Promise((r) => setTimeout(r, backoff));
        }
      }
      if (!succeeded) {
        // Surface a clear, actionable error.
        const why = lastErr?.message || 'network error';
        throw new Error(
          `Upload stalled at part ${i + 1}/${totalParts} after ${MAX_ATTEMPTS} retries (${why}). The file did NOT reach the client. Reconnect to wifi and try Submit again.`
        );
      }
      // Clear any "reconnecting" message now that THIS part succeeded.
      setError('');
      setProgressPct(Math.round(((i + 1) / totalParts) * 100));
    }

    const completeRes = await api.post('/reviews/upload-chunked/complete', {
      upload_id: uploadId,
    });

    // Founder bug Jun 25 2026: Cloudflare 504s on the inline finalise path
    // (cloud-put of 303 MB took >100 s). The backend now returns
    // {processing: true, upload_id, poll_url} and finalises in a
    // background task. We poll until the descriptor is ready or we hit
    // an upper bound (~8 minutes) so the upload-modal can show a real
    // "Finalizing on server…" state instead of a fake spinner.
    if (completeRes.data && completeRes.data.processing) {
      setProgressPct(100);
      setError(''); // clear any "reconnecting" message left over from /part
      setFinaliseStage('Server is queueing the upload…');
      try {
        const desc = await pollChunkedResult(uploadId);
        setFinaliseStage('');
        return desc;
      } catch (e) {
        setFinaliseStage('');
        throw e;
      }
    }
    // Backward-compat: backend returned the descriptor directly
    // (small file / legacy path).
    return completeRes.data;
  };

  /**
   * Poll /upload-chunked/result every ~2.5 s until the server-side
   * finalise pipeline (cloud-put + NAS enqueue + transcode queue)
   * publishes the descriptor. 8-minute upper bound — comfortably
   * accommodates a 1.5 GB upload over a slow cloud-put link while
   * preventing the modal from polling forever if a worker crashes.
   */
  const pollChunkedResult = async (uploadId) => {
    const POLL_INTERVAL_MS = 2500;
    const MAX_WAIT_MS = 8 * 60 * 1000;  // 8 minutes
    const STAGE_LABEL = {
      queued: 'Server is queueing the upload…',
      cloud_put: 'Saving to cloud storage…',
      nas_enqueue: 'Mirroring to studio NAS…',
      done: 'Almost done — wrapping up…',
    };
    const start = Date.now();
    // Allow transient 404s/5xx during pod-failover — the descriptor is
    // in MongoDB so any pod can serve /result, but right after a
    // /complete on a different pod the row may take a hop to land.
    let consecutiveErrors = 0;
    while (Date.now() - start < MAX_WAIT_MS) {
      try {
        const r = await api.post('/reviews/upload-chunked/result', {
          upload_id: uploadId,
        });
        consecutiveErrors = 0;
        if (r.data && r.data.processing === false && r.data.descriptor) {
          // Done!
          return r.data.descriptor;
        }
        // Still processing — surface a clean status to the UI.
        const stage = (r.data && r.data.stage) || 'queued';
        setFinaliseStage(STAGE_LABEL[stage] || 'Server is finishing your upload…');
      } catch (e) {
        const s = e?.response?.status;
        consecutiveErrors += 1;
        // 4xx = real failure surfaced by the server (e.g. failed
        // finaliser). Bail out so the user sees the actual error.
        if (s === 400 || s === 403) {
          throw e;
        }
        // 5xx with the finaliser's error message — surface it.
        if (s === 500) {
          const detail = e?.response?.data?.detail;
          throw new Error(
            typeof detail === 'string'
              ? detail
              : (detail?.message || 'Server-side finalisation failed. Please retry.')
          );
        }
        // 404 = row not yet visible (Atlas replica lag) or session
        // gone. Tolerate a few in a row before giving up.
        if (consecutiveErrors >= 6) {
          throw new Error(
            'Lost contact with the server during finalisation. The file may have uploaded — refresh and check the client dashboard before retrying.'
          );
        }
        setFinaliseStage('Network paused — retrying in a moment…');
      }
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
    throw new Error(
      'Server finalisation took longer than 8 minutes. The upload may still complete in the background — check the client dashboard in a minute before re-submitting.'
    );
  };

  const sizeLabel = file
    ? (file.size > 1024 * 1024 * 1024
        ? `${(file.size / 1024 / 1024 / 1024).toFixed(2)} GB`
        : `${(file.size / 1024 / 1024).toFixed(0)} MB`)
    : '';

  const stage = uploading ? 'upload' : submitting ? 'submit' : (success ? 'done' : 'idle');

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/75 backdrop-blur-sm"
      onClick={stage === 'idle' || stage === 'done' ? onClose : undefined}
      data-testid="episode-lineup-modal"
    >
      <div
        className="bg-zinc-950 border border-amber-500/40 rounded-lg shadow-2xl w-[680px] max-w-[94vw] max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-amber-500/20 bg-gradient-to-r from-amber-950/40 to-zinc-950 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-gradient-to-br from-amber-400 to-rose-500 flex items-center justify-center">
              <Film className="w-4 h-4 text-zinc-950" />
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-widest text-amber-400 font-bold">Episode Lineup</p>
              <h2 className="text-sm text-white font-medium mt-0.5">
                Submit one MP4 for the whole episode <span className="text-zinc-500 text-[11px] font-normal">· {bucketLabel}</span>
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={stage === 'upload' || stage === 'submit'}
            data-testid="episode-lineup-close"
            className="text-zinc-500 hover:text-white p-1.5 rounded hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
          <div className="px-3 py-2.5 bg-amber-500/5 border border-amber-500/20 rounded text-[11px] text-amber-100 leading-relaxed">
            <p className="font-medium mb-0.5">Per-shot review is skipped for this submission.</p>
            <p className="text-amber-300/70 text-[10px]">
              The client opens this in the same review experience as Animatics — they can scrub the timeline, drop pin annotations, and leave comments per frame.
            </p>
          </div>

          {/* Title */}
          <section>
            <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-1.5 block">Title <span className="text-rose-400">*</span></label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="episode-lineup-title"
              className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-[12px] text-white focus:outline-none focus:border-amber-500/60"
              placeholder="Hansel & Gretel EP01 — Full Animation Lineup"
            />
          </section>

          {/* Description */}
          <section>
            <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-1.5 block">Notes to client</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="episode-lineup-description"
              rows={3}
              placeholder="e.g. 'Animation locked through end of Act 2. Awaiting your feedback on the chase sequence (07:30 – 09:15).'"
              className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-[12px] text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/60 resize-none"
            />
          </section>

          {/* File picker */}
          <section>
            <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-1.5 block">Lineup MP4 <span className="text-rose-400">*</span></label>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={stage === 'upload' || stage === 'submit'}
              data-testid="episode-lineup-file-picker"
              className={`w-full px-4 py-6 rounded border-2 border-dashed transition-all flex flex-col items-center gap-2 ${
                file
                  ? 'border-emerald-500/50 bg-emerald-500/5 text-emerald-200'
                  : 'border-zinc-700 hover:border-amber-500/50 hover:bg-amber-500/5 text-zinc-400'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {file ? (
                <>
                  <CheckCircle2 className="w-6 h-6" />
                  <span className="text-[12px] font-medium" data-testid="episode-lineup-file-confirmed">{file.name}</span>
                  <span className="text-[10px] text-zinc-500">{sizeLabel}</span>
                </>
              ) : (
                <>
                  <Upload className="w-6 h-6" />
                  <span className="text-[12px]">Click to pick the assembled episode MP4</span>
                  <span className="text-[10px] text-zinc-500">
                    Recommended ≤ 500 MB · Hard cap 2 GB · MP4 / MOV / WEBM / MKV
                  </span>
                </>
              )}
              <input
                ref={fileRef}
                type="file"
                hidden
                accept=".mp4,.mov,.webm,.m4v,.mkv,video/*"
                onChange={(e) => pickFile(e.target.files?.[0])}
              />
            </button>
            {warn && (
              <div className="mt-2 px-3 py-2 rounded bg-amber-500/10 border border-amber-500/30 text-amber-200 text-[11px] flex items-start gap-2">
                <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> {warn}
              </div>
            )}
          </section>

          {/* Skip-internal toggle for admins / PMs */}
          {canSkipInternal && (
            <section className="px-3 py-2.5 bg-zinc-900 border border-zinc-800 rounded">
              <label className="flex items-start gap-2.5 cursor-pointer" data-testid="episode-lineup-skip-internal-row">
                <input
                  type="checkbox"
                  checked={skipInternal}
                  onChange={(e) => setSkipInternal(e.target.checked)}
                  data-testid="episode-lineup-skip-internal"
                  className="mt-0.5 accent-amber-500"
                />
                <span className="flex-1">
                  <span className="text-[11px] text-white font-medium">Send straight to client (skip Supervisor + PM gates)</span>
                  <span className="block text-[10px] text-zinc-500 mt-0.5">
                    Recommended — the episode lineup is already PM-approved by the time it&apos;s assembled.
                    Uncheck to route through the internal review chain first.
                  </span>
                </span>
              </label>
            </section>
          )}

          {/* Progress + status */}
          {(uploading || submitting) && (
            <section className="px-3 py-2 bg-zinc-900 border border-zinc-800 rounded" data-testid="episode-lineup-progress">
              <div className="flex items-center gap-2 text-[11px] text-zinc-300">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-400" />
                {uploading
                  ? (finaliseStage
                      ? finaliseStage
                      : (file && file.size > CHUNK_THRESHOLD
                          ? `Uploading in 50 MB chunks (Cloudflare-safe)… ${progressPct}%`
                          : `Uploading… ${progressPct}%`))
                  : 'Creating submission…'}
              </div>
              {uploading && (
                <div className="mt-2 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      finaliseStage
                        ? 'bg-gradient-to-r from-amber-400 to-rose-500 animate-pulse'
                        : 'bg-gradient-to-r from-amber-400 to-rose-500'
                    }`}
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              )}
              {finaliseStage && (
                <p className="mt-1.5 text-[10px] text-zinc-500" data-testid="episode-lineup-finalise-hint">
                  Bytes are uploaded — server is now writing to cloud + studio NAS. This can take up to a minute or two for large files. Keep this tab open.
                </p>
              )}
            </section>
          )}

          {success && (
            <div className="px-3 py-2.5 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-[11px] flex items-start gap-2" data-testid="episode-lineup-success">
              <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {success}
            </div>
          )}
          {error && (
            <div className="px-3 py-2.5 rounded bg-rose-500/10 border border-rose-500/30 text-rose-200 text-[11px] flex items-start gap-2" data-testid="episode-lineup-error">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-zinc-800 bg-zinc-950 flex items-center justify-between gap-3">
          <p className="text-[9px] text-zinc-600">
            The client reviews this in the same UI as Animatics — pin annotations + frame comments.
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={stage === 'upload' || stage === 'submit'}
              data-testid="episode-lineup-cancel"
              className="px-3 py-1.5 text-[11px] text-zinc-300 hover:text-white bg-zinc-900 border border-zinc-800 hover:border-zinc-600 rounded disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {success ? 'Close' : 'Cancel'}
            </button>
            {!success && (
              <button
                onClick={submit}
                disabled={!file || !title.trim() || stage === 'upload' || stage === 'submit'}
                data-testid="episode-lineup-submit"
                className={`px-4 py-1.5 text-[11px] font-medium rounded flex items-center gap-1.5 transition-all ${
                  !file || !title.trim() || stage === 'upload' || stage === 'submit'
                    ? 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
                    : 'bg-gradient-to-r from-amber-400 to-rose-500 hover:from-amber-300 hover:to-rose-400 text-zinc-950'
                }`}
              >
                {stage === 'upload' || stage === 'submit' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                Submit Lineup
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
